import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, Truck, FileText, MapPin, Clock, CheckCircle, AlertCircle, Plus, Eye } from 'lucide-react';
import api from '../services/api';

const CustomerPortalPage = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [account, setAccount] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showTrackDialog, setShowTrackDialog] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  
  // New order form
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [orderSubmitting, setOrderSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, invoicesRes, deliveriesRes, productsRes] = await Promise.all([
        api.get('/portal/my-orders').catch(() => ({ data: { orders: [] } })),
        api.get('/portal/my-invoices').catch(() => ({ data: { invoices: [], outstanding_balance: 0 } })),
        api.get('/portal/my-deliveries').catch(() => ({ data: [] })),
        api.get('/products/').catch(() => ({ data: [] }))
      ]);
      
      setOrders(ordersRes.data.orders || []);
      setInvoices(invoicesRes.data.invoices || []);
      setOutstandingBalance(invoicesRes.data.outstanding_balance || 0);
      setDeliveries(Array.isArray(deliveriesRes.data) ? deliveriesRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      
      // Try to get account info
      try {
        const accountRes = await api.get('/portal/my-account');
        setAccount(accountRes.data);
      } catch {
        // Not a customer account, that's OK
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handlePlaceOrder = async () => {
    if (!selectedSite || !selectedProduct || !quantity) return;
    
    setOrderSubmitting(true);
    try {
      await api.post(`/portal/place-order?site_id=${selectedSite}&product_id=${selectedProduct}&quantity=${quantity}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      setShowOrderDialog(false);
      setSelectedSite('');
      setSelectedProduct('');
      setQuantity('');
      setNotes('');
      fetchData();
    } catch (error) {
      console.error('Error placing order:', error);
      alert(error.response?.data?.detail || 'Failed to place order');
    }
    setOrderSubmitting(false);
  };

  const handleTrackOrder = async (orderId) => {
    try {
      const res = await api.get(`/portal/track-order/${orderId}`);
      setTrackingData(res.data);
      setShowTrackDialog(true);
    } catch (error) {
      console.error('Error tracking order:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      planned: 'bg-indigo-100 text-indigo-800',
      dispatched: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      invoiced: 'bg-slate-100 text-slate-800',
      cancelled: 'bg-red-100 text-red-800',
      paid: 'bg-emerald-100 text-emerald-800',
      overdue: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-slate-100 text-slate-800'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-portal">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:border-orange-500/50 transition-colors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Orders</p>
                <p className="text-2xl font-bold">{orders.filter(o => !['delivered', 'invoiced', 'cancelled'].includes(o.status)).length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:border-orange-500/50 transition-colors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Deliveries</p>
                <p className="text-2xl font-bold">{deliveries.filter(d => d.status !== 'completed').length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:border-orange-500/50 transition-colors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                <p className="text-2xl font-bold">£{outstandingBalance.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:border-orange-500/50 transition-colors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sites</p>
                <p className="text-2xl font-bold">{account?.sites?.length || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Customer Portal</CardTitle>
            <CardDescription>Manage your orders, deliveries, and invoices</CardDescription>
          </div>
          <Button 
            className="bg-orange-500 hover:bg-orange-400 text-white"
            onClick={() => setShowOrderDialog(true)}
            data-testid="place-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Place Order
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="deliveries" data-testid="tab-deliveries">Deliveries</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            </TabsList>
            
            {/* Orders Tab */}
            <TabsContent value="orders" className="mt-4">
              <div className="space-y-3">
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No orders found. Place your first order!
                  </div>
                ) : (
                  orders.map(order => (
                    <div 
                      key={order.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:border-orange-500/50 transition-colors"
                      data-testid={`order-${order.order_number}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{order.order_number}</span>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {order.site_name} • {order.lines?.[0]?.product_name} • {order.lines?.[0]?.quantity?.toLocaleString()} L
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(order.created_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-lg">£{order.grand_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleTrackOrder(order.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Track
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
            
            {/* Deliveries Tab */}
            <TabsContent value="deliveries" className="mt-4">
              <div className="space-y-3">
                {deliveries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No deliveries found.
                  </div>
                ) : (
                  deliveries.map(delivery => (
                    <div 
                      key={delivery.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:border-orange-500/50 transition-colors"
                      data-testid={`delivery-${delivery.delivery_number}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{delivery.delivery_number}</span>
                          {getStatusBadge(delivery.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {delivery.site_name}
                        </p>
                        {delivery.driver_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Driver: {delivery.driver_name}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm">
                          {delivery.lines?.[0]?.delivered_quantity?.toLocaleString() || 0} / {delivery.lines?.[0]?.ordered_quantity?.toLocaleString() || 0} L
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {delivery.completed_at ? new Date(delivery.completed_at).toLocaleDateString('en-GB') : 'Pending'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
            
            {/* Invoices Tab */}
            <TabsContent value="invoices" className="mt-4">
              <div className="space-y-3">
                {invoices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No invoices found.
                  </div>
                ) : (
                  invoices.map(invoice => (
                    <div 
                      key={invoice.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:border-orange-500/50 transition-colors"
                      data-testid={`invoice-${invoice.invoice_number}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{invoice.invoice_number}</span>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : 'N/A'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">£{invoice.grand_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                        {invoice.paid_amount > 0 && invoice.paid_amount < invoice.grand_total && (
                          <p className="text-xs text-muted-foreground">
                            Paid: £{invoice.paid_amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Place Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Place New Order</DialogTitle>
            <DialogDescription>Order fuel for delivery to your site</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Delivery Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="select-site">
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {account?.sites?.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} - {site.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger data-testid="select-product">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.is_active).map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (£{product.base_price?.toFixed(4)}/L)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Quantity (Litres)</Label>
              <Input
                type="number"
                placeholder="e.g. 5000"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="input-quantity"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Any special instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="input-notes"
              />
            </div>
            
            <Button 
              className="w-full bg-orange-500 hover:bg-orange-400 text-white"
              onClick={handlePlaceOrder}
              disabled={!selectedSite || !selectedProduct || !quantity || orderSubmitting}
              data-testid="submit-order-btn"
            >
              {orderSubmitting ? 'Placing Order...' : 'Place Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Track Order Dialog */}
      <Dialog open={showTrackDialog} onOpenChange={setShowTrackDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Order Tracking</DialogTitle>
            <DialogDescription>
              {trackingData?.order?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {trackingData && (
              <div className="space-y-4">
                {/* Timeline */}
                <div className="relative">
                  {trackingData.timeline?.map((step, index) => (
                    <div key={step.status} className="flex items-start gap-3 pb-6 last:pb-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step.completed ? 'bg-green-500' : 'bg-slate-200'
                      }`}>
                        {step.completed ? (
                          <CheckCircle className="w-5 h-5 text-white" />
                        ) : (
                          <Clock className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${step.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {step.label}
                        </p>
                      </div>
                      {index < trackingData.timeline.length - 1 && (
                        <div className={`absolute left-4 top-8 w-0.5 h-6 ${
                          step.completed ? 'bg-green-500' : 'bg-slate-200'
                        }`} style={{ marginTop: `${index * 48}px` }} />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Delivery Info */}
                {trackingData.delivery && (
                  <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <p className="text-sm font-medium">Delivery Details</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Driver: {trackingData.delivery.driver_name || 'Assigned'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Status: {trackingData.delivery.status}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalPage;
